import React, { useState } from "react";
import { Box, Button, Divider, Typography, styled } from "@mui/material";
import { HatInfo } from "./HatInfo";
import { ClusterAvatarGroup } from "./ClusterAvatarGroup";
import { PathwaySection } from "./PathwaySection";
import { HatViewMoreDialog } from "./HatViewMoreDialog";
import type { MSHatData, RelatedHat } from "../../types";

interface LeftPanelProps {
  hat: MSHatData;
  onHatClick?: (hatId: string) => void;
  onPathwayClick?: (pathwayId: string) => void;
  onClusterClick?: (clusterId: string) => void;
  demandRegionName?: string;
  demandLabel?: string;
  alsoLikeImagesLoading?: boolean;
}

const PanelContainer = styled(Box, {
  label: "LeftPanelContainer",
})(() => ({
  width: 368,
  flexShrink: 0,
  height: 569,
  display: "flex",
  flexDirection: "column",
  gap: 8,
  overflow: "visible",
}));

const Section = styled(Box, {
  label: "LeftPanelSection",
})(() => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 16,
  width: "100%",
}));

const SectionHeading = styled(Typography, {
  label: "LeftPanelSectionHeading",
})(() => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontSize: "16px",
  fontWeight: 600,
  lineHeight: "24px",
  color: "#ffffff",
  textAlign: "center",
}));

const dividerSx = { borderColor: "rgba(145, 158, 171, 0.2)", width: "100%" };

// H&L brand cap icon for the "Hats you might also like" button (from Figma).
const CapIcon = () => (
  <svg width="24" height="18" viewBox="0 0 24 18.225" fill="none">
    <path
      d="M18.9 11.25C18.675 11.175 18.4875 11.1375 18.2625 11.0625C18.1125 11.025 17.9625 11.025 17.85 10.9875C17.475 7.5 15.6375 4.5 13.05 2.8125C16.125 4.1625 18.4875 7.3875 18.9 11.25ZM17.625 14.1375C16.1625 14.5875 14.625 15.4125 13.05 16.575C10.05 18.7875 6.8625 16.65 4.95 14.775C5.325 14.7375 5.7 14.7 6.1125 14.625H6.15C7.0875 14.475 8.0625 14.2125 9.0375 13.8375L9.225 13.7625C13.575 12.1875 15.75 11.3625 18.15 11.775C18.4875 11.85 18.8625 11.9625 19.1625 12.1125C19.3875 12.225 19.575 12.3375 19.7625 12.525L21.45 13.875C20.25 13.65 18.975 13.725 17.625 14.1375ZM18.75 14.5875C20.2125 14.325 21.5625 14.475 22.725 15.075C21.3 15.4125 19.875 15.1125 18.75 14.5875ZM24 15.15C24 15.1125 24 15.075 23.9625 15.0375C23.9625 15.0375 23.9625 15.0375 23.9625 15C23.9625 14.9625 23.925 14.9625 23.8875 14.925L20.25 12C20.0625 11.85 19.875 11.7375 19.6875 11.625C19.3125 6.375 15.5625 2.175 11.025 1.575C10.875 0.675 10.125 0 9.1875 0C8.2125 0 7.3875 0.7875 7.35 1.7625C2.5125 2.7375 0 6.5625 0 12.9V13.6125V13.65V13.725C0 13.7625 0.0375 13.7625 0.0375 13.8L0.075 13.8375C0.075 13.875 0.1125 13.875 0.15 13.875C0.15 13.875 0.15 13.9125 0.1875 13.9125C1.2375 14.475 2.5125 14.8125 3.9 14.8125C5.1 16.0875 7.575 18.225 10.3125 18.225C11.325 18.225 12.4125 17.925 13.425 17.1375C14.8875 16.05 16.3125 15.2625 17.625 14.85C19.125 15.675 21.375 16.425 23.7 15.4875H23.7375C23.775 15.45 23.8125 15.45 23.85 15.4125C23.8875 15.375 23.8875 15.3375 23.925 15.3V15.2625V15.225C24 15.225 24 15.1875 24 15.15ZM9.15 0.7125C9.6375 0.7125 10.05 1.05 10.2 1.5C10.0125 1.5 9.8625 1.5 9.675 1.5C9.1125 1.5 8.55 1.575 8.025 1.65C8.175 1.0875 8.625 0.7125 9.15 0.7125ZM4.0875 14.1C2.5125 14.1 1.425 13.725 0.7125 13.35C0.7125 13.2 0.7125 13.05 0.7125 12.9C0.7125 8.5875 1.9875 3.4875 7.6875 2.4C5.1 4.8375 4.35 8.8875 5.55 13.9875C5.0625 14.0625 4.575 14.1 4.0875 14.1ZM8.9625 13.0875L8.775 13.1625C7.9125 13.4625 7.0875 13.725 6.3 13.875C5.025 8.5125 6 4.3875 9.075 2.25C9.225 2.25 9.375 2.25 9.525 2.2125C13.425 2.8875 16.575 6.525 17.1 10.95C14.925 10.9125 12.75 11.7 8.9625 13.0875Z"
      fill="#322E63"
    />
    <path
      d="M10.2375 1.4625C10.05 1.4625 9.9 1.4625 9.7125 1.4625C9.15 1.4625 8.5875 1.5375 8.0625 1.6125C8.175 1.0875 8.625 0.7125 9.15 0.7125C9.675 0.7125 10.0875 1.05 10.2375 1.4625Z"
      fill="#00B8D9"
    />
    <path
      d="M22.7625 15.075C21.3 15.4125 19.9125 15.075 18.7875 14.5875C20.2125 14.325 21.5625 14.475 22.7625 15.075Z"
      fill="#00B8D9"
    />
    <path
      d="M21.4125 13.875C20.2125 13.65 18.9375 13.725 17.5875 14.1375C16.125 14.5875 14.5875 15.4125 13.0125 16.575C10.0125 18.7875 6.825 16.65 4.9125 14.775C5.2875 14.7375 5.6625 14.7 6.075 14.625H6.1125C7.05 14.475 8.025 14.2125 9 13.8375L9.1875 13.7625C13.575 12.1875 15.75 11.3625 18.15 11.775C18.4875 11.85 18.8625 11.9625 19.1625 12.1125C19.3875 12.225 19.575 12.3375 19.7625 12.525L21.4125 13.875Z"
      fill="#00B8D9"
    />
    <path
      d="M21.4125 13.875C20.55 13.725 19.65 13.6875 18.75 13.875L17.1 12.5625C16.9125 12.4125 16.725 12.2625 16.5 12.15C16.2 12 15.8625 11.8875 15.525 11.8125C16.4625 11.6625 17.2875 11.6625 18.15 11.8125C18.4875 11.8875 18.8625 12 19.1625 12.15C19.3875 12.2625 19.575 12.375 19.7625 12.5625L21.4125 13.875Z"
      fill="#00B8D9"
    />
    <path
      d="M17.1 10.9503C14.9625 10.8753 12.75 11.7003 8.9625 13.0878L8.775 13.1628C7.9125 13.4628 7.0875 13.7253 6.3 13.8753C5.025 8.51279 6 4.38779 9.075 2.25029C9.225 2.25029 9.375 2.25029 9.525 2.21279C13.425 2.88779 16.5375 6.52529 17.1 10.9503Z"
      fill="#FFDC7B"
    />
    <path
      d="M18.9 11.25C18.675 11.175 18.4875 11.1375 18.2625 11.0625C18.1125 11.025 17.9625 11.025 17.85 10.9875C17.475 7.5 15.6375 4.5 13.05 2.8125C16.125 4.1625 18.4875 7.3875 18.9 11.25Z"
      fill="#FFD100"
    />
    <path
      d="M5.5875 13.9875C5.1 14.0625 4.575 14.1 4.125 14.1H4.0875C2.55 14.1 1.4625 13.725 0.75 13.35C0.75 13.2 0.75 13.05 0.75 12.9C0.75 8.5875 2.025 3.4875 7.725 2.4C5.1375 4.875 4.3875 8.8875 5.5875 13.9875Z"
      fill="#FFE8B3"
    />
  </svg>
);

export const LeftPanel = ({
  hat,
  onHatClick,
  onPathwayClick,
  onClusterClick,
  demandRegionName,
  demandLabel,
  alsoLikeImagesLoading,
}: LeftPanelProps) => {
  const [hatsOpen, setHatsOpen] = useState(false);

  const relatedHats: RelatedHat[] = (hat.alsoLike || [])
    .filter((item) => item.relatedHat)
    .map((item) => item.relatedHat);

  const clusters = hat.msCareerCluster || [];

  return (
    <PanelContainer>
      <HatInfo
        displayName={hat.displayName}
        description={hat.msTextOverview || hat.msShortDescription}
        demandPercentChange={hat.growth?.percentChange}
        demandLabel={demandLabel}
        demandRegionName={demandRegionName}
      />

      <Divider sx={dividerSx} />

      <Section>
        <SectionHeading>Clusters &amp; Pathways</SectionHeading>
        {clusters.length > 0 && (
          <ClusterAvatarGroup
            clusters={clusters}
            onClusterClick={onClusterClick}
          />
        )}
        <PathwaySection
          pathways={hat.msPathway}
          whatCanIDo={hat.msWhatCanIDo}
          onPathwayClick={onPathwayClick}
        />
      </Section>

      {relatedHats.length > 0 && (
        <>
          <Divider sx={dividerSx} />
          <Section>
            <Button
              variant="outlined"
              onClick={() => setHatsOpen(true)}
              startIcon={<CapIcon />}
              sx={{
                backgroundColor: "#ffffff",
                color: "#1c252e",
                borderColor: "rgba(145, 158, 171, 0.32)",
                textTransform: "none",
                fontFamily: "Proxima Nova, sans-serif",
                fontWeight: 700,
                fontSize: "15px",
                lineHeight: "26px",
                borderRadius: "8px",
                height: 48,
                px: 2,
                "&:hover": {
                  backgroundColor: "#f4f6f8",
                  borderColor: "rgba(145, 158, 171, 0.48)",
                },
              }}
            >
              Hats you might also like
            </Button>
          </Section>
          <HatViewMoreDialog
            open={hatsOpen}
            onClose={() => setHatsOpen(false)}
            hats={relatedHats}
            onHatClick={onHatClick}
            imagesLoading={alsoLikeImagesLoading}
          />
        </>
      )}
    </PanelContainer>
  );
};
